import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@/assets/icons';
import {
  Button,
  Calendar,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SvgIcon,
} from '@/components/common';
import { formatDate, getDate, mergeClasses } from '@/utils';

import { DEFAULT_MAX_YEAR, DEFAULT_MIN_YEAR } from './DateRangePicker.constants';
import { IDateRange, IDateRangePicker, IDateRangePickerRef } from './DateRangePicker.interfaces';

/** The DateRangePicker component allows a user to select a range of dates */
const DateRangePicker = forwardRef<IDateRangePickerRef, IDateRangePicker>(
  (
    {
      initialDateFrom,
      initialDateTo,
      onUpdate,
      align = 'end',
      locale = 'en-GB',
      minYear = DEFAULT_MIN_YEAR,
      maxYear = DEFAULT_MAX_YEAR,
      className,
      label,
      required,
      error,
      disabled,
      prefixIcon,
      hasSuffixIcon,
      buttonClassName,
      placeholder,
      allowClear,
      calendarDisabled,
      isModal = false,
    }: IDateRangePicker,
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const [range, setRange] = useState<IDateRange>({
      from: getDate(initialDateFrom),
      to: getDate(initialDateTo),
    });

    // To prevent onUpdate from firing on initial render
    const isInitialRender = useRef(true);

    useEffect(() => {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }
      onUpdate?.({ range });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const onClear = (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      setRange({ from: undefined, to: undefined });
      onUpdate?.({ range });
    };

    useImperativeHandle(ref, () => ({
      onClear: onClear,
    }));

    return (
      <div className={mergeClasses('flex flex-col gap-1.5', className)}>
        {label && (
          <Label className="!gap-0 font-semibold">
            {label}
            {required && <span className="ml-1 text-semantic-error-base">*</span>}
          </Label>
        )}
        <Popover modal={isModal} open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              borderRadius="soft"
              className={mergeClasses(
                'w-fit focus:shadow-none border-neutral-n4',
                // Data state: open
                'data-[state=open]:border-brand-primary-base',
                // Prefix icon spacing
                prefixIcon && '!justify-start',
                // Suffix icon spacing
                (hasSuffixIcon || (allowClear && (range.from || range.to))) && '!justify-between',
                error && 'border-semantic-error-base',
                buttonClassName,
              )}
              prefixIcon={prefixIcon}
              suffixIcon={
                <div className="flex items-center gap-1">
                  {allowClear && (range.from || range.to) && (
                    <CloseIcon
                      className="!size-4 cursor-pointer text-neutral-n6 hover:text-brand-primary-darkest"
                      onClick={() => onClear()}
                    />
                  )}
                  <SvgIcon
                    className={mergeClasses(
                      'w-5 h-5 cursor-pointer text-brand-primary-darkest',
                      isOpen && '!text-brand-primary-base',
                    )}
                    component={isOpen ? ChevronUpIcon : ChevronDownIcon}
                  />
                </div>
              }
              disabled={disabled}
            >
              <div
                className={mergeClasses(
                  'font-medium',
                  !range.from || !range.to ? 'text-neutral-n7' : 'text-brand-primary-darkest',
                )}
              >
                {placeholder && !range.from && !range.to
                  ? placeholder
                  : `${formatDate({
                      date: range.from,
                      defaultText: 'Start Date',
                      locale,
                    })} - ${formatDate({
                      date: range.to,
                      defaultText: 'End Date',
                      locale,
                    })}`}
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent align={align} className="w-auto p-none">
            <div className="flex">
              <div className="flex">
                <div className="flex flex-col">
                  <div>
                    <Calendar
                      mode="range"
                      onSelect={(value: { from?: Date; to?: Date } | undefined) => {
                        if (!value?.from || !value?.to) return;
                        // Start a new range selection if 'from' date is earlier than current 'from' date or both dates are set
                        // The selected date is the one that is not equal to the current 'from' date
                        const selectedDate = range.from === value.from ? value.to : value.from;
                        if (!range.from || range.from > selectedDate || (range.from && range.to)) {
                          setRange({ from: selectedDate, to: undefined });
                          return;
                        }
                        const to = value.to;
                        to.setHours(23, 59, 59, 999); // Set to end of day
                        setRange({ from: value.from, to: to });
                        setIsOpen(false);
                      }}
                      selected={range}
                      numberOfMonths={1}
                      defaultMonth={new Date()}
                      startMonth={new Date(minYear, 0)}
                      endMonth={new Date(maxYear, 11)}
                      captionLayout="dropdown"
                      footer
                      disabled={calendarDisabled}
                    />
                  </div>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {error && <p className="text-xs text-semantic-error-base">{error}</p>}
      </div>
    );
  },
);

DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker, type IDateRange, type IDateRangePicker };
